import "./datatable.scss";
import "./filterSummary.css"; // La importación a nivel local como solicitaste
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { userColumns } from "../../datatablesource";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  doc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import customLocaleText from './LocaleText.js';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, CircularProgress, TextField, InputAdornment, Typography, Button, Divider, Chip } from '@mui/material';
// Importaciones
import VigilanteFilters from '../vigilantes/VigilanteFilters';
import LoadAllButton from '../vigilantes/LoadAllButton';
import AdminVigilantesButton from '../vigilantes/AdminVigilantesButton'; // Importar el nuevo botón
import { 
  fetchVigilantesFiltrados, 
  updateVigilanteEstado,
  fetchAllVigilantes      // Importar la nueva función
} from '../vigilantes/vigilanteService';
import datatableProfiler from './utils/DatatablePerformanceProfiler';

/**
 * Componente para mostrar una tabla de datos de vigilantes.
 */
const Datatable = () => {
  // Estados para los datos
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState('');

  // Estado para mostrar/ocultar borrados
  const [showDeleted, setShowDeleted] = useState(false);

  // Estado para filas seleccionadas
  const [selectedRows, setSelectedRows] = useState([]);

  // Estado de carga
  const [isLoading, setIsLoading] = useState(false);

  // Estado para la fuente de datos actual
  const [currentDataSource, setCurrentDataSource] = useState('');

  // Filtros avanzados
  const [advancedFilters, setAdvancedFilters] = useState({
    clientes: [],
    servicios: []
  });

  // Contexto de autenticación
  const { currentUser, currentService, adminData } = useContext(AuthContext);

  // Estado para mostrar mensajes de error
  const [error, setError] = useState('');

  /**
   * Función para aplicar filtros avanzados
   * @param {Object} filters - Filtros a aplicar
   */
  const handleFilterApply = (filters) => {
    setAdvancedFilters(filters);
    setCurrentDataSource('filtered');
    fetchFilteredData(filters);
  };

  /**
   * Función para cargar datos filtrados desde Firebase
   * @param {Object} filters - Filtros a aplicar
   */
  const fetchFilteredData = async (filters) => {
    if (!currentService?.id) {
      console.error("Información de servicio no disponible.");
      setError("Información de servicio no disponible. Intente recargar la página.");
      return;
    }

    setIsLoading(true);
    setError('');
    
    // 📊 Iniciar sesión de profiling
    datatableProfiler.startSession('Datatable_FetchFiltered', {
      serviceId: currentService.id,
      filters,
      clientesPermitidos: adminData?.clientes?.length || 0,
      tiendasPermitidas: adminData?.tiendas?.length || 0
    });

    try {
      // Verificar si tenemos filtros activos
      const hasClienteFilter = filters.clientes && filters.clientes.length > 0;
      const hasServicioFilter = filters.servicios && filters.servicios.length > 0;
      const showAll = filters.cliente === 'todos';

      if (!hasClienteFilter && !hasServicioFilter && !showAll) {
        // Si no hay filtros y no es "mostrar todos", no cargamos datos
        setData([]);
        setFilteredData([]);
        setIsLoading(false);
        
        // Finalizar sesión sin datos
        datatableProfiler.endSession({
          vigilantesCargados: 0,
          razon: 'sin_filtros_activos'
        });
        return;
      }

      // 📊 Medir carga de vigilantes
      datatableProfiler.startMeasurement('01_FetchVigilantes', {
        hasClienteFilter,
        hasServicioFilter,
        showAll,
        clientesCount: filters.clientes?.length || 0,
        serviciosCount: filters.servicios?.length || 0
      });

      // Usar el servicio para obtener vigilantes filtrados
      const vigilantesList = await fetchVigilantesFiltrados({
        serviceId: currentService.id,
        filters,
        clientesPermitidos: adminData?.clientes || [],
        tiendasPermitidas: adminData?.tiendas || []
      });

      datatableProfiler.endMeasurement('01_FetchVigilantes', {
        vigilantesCargados: vigilantesList.length,
        conTiendas: vigilantesList.filter(v => v.tiendas?.length > 0).length
      });

      // 📊 Medir actualización de estado
      datatableProfiler.startMeasurement('02_UpdateState');

      setData(vigilantesList);

      // Esperar a que React actualice
      setTimeout(() => {
        datatableProfiler.endMeasurement('02_UpdateState', {
          vigilantesEnEstado: vigilantesList.length
        });

        // 📊 Finalizar sesión
        datatableProfiler.endSession({
          vigilantesCargados: vigilantesList.length,
          tipoFiltro: hasClienteFilter ? 'cliente' : hasServicioFilter ? 'servicio' : 'todos',
          success: true
        });
      }, 0);

    } catch (err) {
      console.error("Error al cargar los datos:", err);
      setError(`Error al cargar los datos: ${err.message}`);
      
      // Finalizar sesión con error
      datatableProfiler.endSession({
        error: err.message,
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja el evento para cargar todos los vigilantes sin filtros de permisos
   */
  const handleLoadAllVigilantes = async () => {
    if (!currentService?.id) {
      console.error("Información de servicio no disponible.");
      setError("Información de servicio no disponible. Intente recargar la página.");
      return;
    }

    setIsLoading(true);
    setCurrentDataSource('all');
    setError('');
    
    // 📊 Iniciar sesión de profiling
    datatableProfiler.startSession('Datatable_LoadAll', {
      serviceId: currentService.id,
      sinFiltros: true
    });

    try {
      // 📊 Medir carga de todos los vigilantes
      datatableProfiler.startMeasurement('03_FetchAllVigilantes', {
        sinRestricciones: true
      });

      // Obtener TODOS los vigilantes sin restricciones
      const allVigilantes = await fetchAllVigilantes(currentService.id);

      datatableProfiler.endMeasurement('03_FetchAllVigilantes', {
        vigilantesCargados: allVigilantes.length,
        conTiendas: allVigilantes.filter(v => v.tiendas?.length > 0).length
      });

      // 📊 Medir actualización de estado
      datatableProfiler.startMeasurement('04_UpdateState');

      setData(allVigilantes);
      setAdvancedFilters({ cliente: 'todos', clientes: [], servicios: [] });

      // Esperar a que React actualice
      setTimeout(() => {
        datatableProfiler.endMeasurement('04_UpdateState', {
          vigilantesEnEstado: allVigilantes.length
        });

        // 📊 Finalizar sesión
        datatableProfiler.endSession({
          vigilantesCargados: allVigilantes.length,
          tipoOperacion: 'cargar_todos',
          success: true
        });
      }, 0);

    } catch (err) {
      console.error("Error al cargar todos los vigilantes:", err);
      setError(`Error al cargar todos los vigilantes: ${err.message}`);
      
      // Finalizar sesión con error
      datatableProfiler.endSession({
        error: err.message,
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja el evento para cargar solo los vigilantes relacionados con el admin actual
   * (Comportamiento original)
   */
  const handleLoadAdminVigilantes = async () => {
    if (!currentService?.id) {
      console.error("Información de servicio no disponible.");
      setError("Información de servicio no disponible. Intente recargar la página.");
      return;
    }

    setIsLoading(true);
    setCurrentDataSource('admin');
    setError('');
    
    // 📊 Iniciar sesión de profiling
    datatableProfiler.startSession('Datatable_LoadAdmin', {
      serviceId: currentService.id,
      clientesPermitidos: adminData?.clientes?.length || 0,
      tiendasPermitidas: adminData?.tiendas?.length || 0
    });

    try {
      // 📊 Medir carga de vigilantes del admin
      datatableProfiler.startMeasurement('05_FetchAdminVigilantes', {
        clientesPermitidos: adminData?.clientes?.length || 0,
        tiendasPermitidas: adminData?.tiendas?.length || 0
      });

      // Obtener vigilantes relacionados con el admin actual (comportamiento original)
      const adminVigilantes = await fetchVigilantesFiltrados({
        serviceId: currentService.id,
        filters: { cliente: 'todos' },
        clientesPermitidos: adminData?.clientes || [],
        tiendasPermitidas: adminData?.tiendas || []
      });

      datatableProfiler.endMeasurement('05_FetchAdminVigilantes', {
        vigilantesCargados: adminVigilantes.length,
        conTiendas: adminVigilantes.filter(v => v.tiendas?.length > 0).length
      });

      // 📊 Medir actualización de estado
      datatableProfiler.startMeasurement('06_UpdateState');
      
      setData(adminVigilantes);
      setAdvancedFilters({ cliente: 'admin' });

      // Esperar a que React actualice
      setTimeout(() => {
        datatableProfiler.endMeasurement('06_UpdateState', {
          vigilantesEnEstado: adminVigilantes.length
        });

        // 📊 Finalizar sesión
        datatableProfiler.endSession({
          vigilantesCargados: adminVigilantes.length,
          tipoOperacion: 'cargar_admin',
          success: true
        });
      }, 0);

    } catch (err) {
      console.error("Error al cargar los vigilantes del admin:", err);
      setError(`Error al cargar los vigilantes del admin: ${err.message}`);
      
      // Finalizar sesión con error
      datatableProfiler.endSession({
        error: err.message,
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizar datos filtrados cuando cambia la búsqueda o el estado de borrado
  useEffect(() => {
    const updateFilteredData = () => {
      // Filtrar por texto de búsqueda y estado de borrado
      const updatedFilteredData = data
        .filter((row) => {
          const rowValues = Object.values(row).join('').toLowerCase();
          return rowValues.includes(filter.toLowerCase());
        })
        .filter((row) => {
          if (showDeleted) {
            return row.borrado; // Mostrar solo los registros borrados si showDeleted es true
          } else {
            return !row.borrado; // Mostrar solo los registros no borrados si showDeleted es false
          }
        })
        .map((row) => {
          if (Array.isArray(row.tiendas)) {
            return { ...row, tiendas: row.tiendas.map((tienda) => `${tienda.numero}`) };
          } else {
            return row;
          }
        });
      setFilteredData(updatedFilteredData);
    };

    updateFilteredData();
  }, [data, filter, showDeleted]);

  /**
   * Maneja el evento de eliminación de un vigilante.
   * @param {string|Array} id - ID o array de IDs de vigilantes a eliminar.
   */
  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar los usuarios seleccionados?")) {
      console.log("ids to delete: ", id);

      try {
        if (Array.isArray(id)) {
          // Usar Promise.all para ejecutar todas las actualizaciones en paralelo
          await Promise.all(id.map(idRow =>
            updateVigilanteEstado(currentService.id, idRow, true)
          ));
        } else {
          await updateVigilanteEstado(currentService.id, id, true);
        }
        
        // Recargar los datos según la fuente actual
        if (currentDataSource === 'admin') {
          handleLoadAdminVigilantes();
        } else if (currentDataSource === 'all') {
          handleLoadAllVigilantes();
        } else {
          fetchFilteredData(advancedFilters);
        }
      } catch (err) {
        console.log("Error al eliminar usuarios:", err);
        setError(`Error al eliminar usuarios: ${err.message}`);
      }
    }
  };

  /**
   * Maneja el cambio en la casilla de verificación "Mostrar borrados".
   * @param {object} event - Evento de cambio.
   */
  const handleShowDeletedChange = (event) => {
    setShowDeleted(event.target.checked);
  };

  /**
   * Maneja la selección/deselección de filas.
   * @param {Array} newSelectionModel - Nuevo modelo de selección.
   */
  const onSelectionModelChange = (newSelectionModel) => {
    console.log('Selección actualizada:', newSelectionModel);
    setSelectedRows(newSelectionModel);
  };

  /**
   * Maneja el evento de restauración de un vigilante.
   * @param {string|Array} id - ID o array de IDs de vigilantes a restaurar.
   */
  const handleRestore = async (id) => {
    const confirmRestore = window.confirm("¿Estás seguro de que deseas recuperar este usuario?");
    console.log("ids to restore: ", id);

    if (confirmRestore) {
      try {
        if (Array.isArray(id)) {
          // Usar Promise.all para ejecutar todas las actualizaciones en paralelo
          await Promise.all(id.map(idRow =>
            updateVigilanteEstado(currentService.id, idRow, false)
          ));
        } else {
          await updateVigilanteEstado(currentService.id, id, false);
        }
        
        // Recargar los datos según la fuente actual
        if (currentDataSource === 'admin') {
          handleLoadAdminVigilantes();
        } else if (currentDataSource === 'all') {
          handleLoadAllVigilantes();
        } else {
          fetchFilteredData(advancedFilters);
        }
      } catch (err) {
        console.log("Error al restaurar usuarios:", err);
        setError(`Error al restaurar usuarios: ${err.message}`);
      }
    }
  };

  // Columna de acciones para cada fila
  const actionColumn = {
    field: "action",
    headerName: "Action",
    width: 250,
    renderCell: (params) => {
      return (
        <div className="cellAction">
          <Link
            to={`/vigilantes/${encodeURIComponent(params.row.id)}`}
            style={{ textDecoration: "none" }}
          >
            <div className="viewButton">Ver</div>
          </Link>
          <Link to={`/vigilantes/update/${encodeURIComponent(params.row.id)}`}
            style={{ textDecoration: "none" }}>
            <EditIcon className="editIcon" /> {/* Ícono de lápiz para editar */}
          </Link>
          {params.row.borrado ? (
            <div
              className="viewButton"
              onClick={() => handleRestore(Array.isArray(selectedRows) && selectedRows.length > 0 ? selectedRows : params.row.id)}
            >
              Recuperar
            </div>
          ) : null}
          {!params.row.borrado ? (
            <DeleteIcon
              className="deleteIcon"
              onClick={() => handleDelete(Array.isArray(selectedRows) && selectedRows.length > 0 ? selectedRows : params.row.id)}
            />
          ) : null}
        </div>
      );
    },
  };

  // Renderizar etiquetas de filtro
  const renderFilterTags = () => {
    const { clientes, servicios } = advancedFilters;
    const hasClientes = clientes && clientes.length > 0;
    const hasServicios = servicios && servicios.length > 0;
    
    if (currentDataSource === 'all') {
      return <span className="filter-tag">Todos los usuarios (sin filtros)</span>;
    } else if (currentDataSource === 'admin') {
      return <span className="filter-tag">Usuarios con mis permisos</span>;
    } else if (advancedFilters.cliente === 'todos') {
      return <span className="filter-tag">Usuarios filtrados</span>;
    }

    return (
      <>
        {hasClientes && clientes.map(cliente => (
          <span key={cliente} className="filter-tag">Cliente: {cliente}</span>
        ))}
        {hasServicios && servicios.map(servicio => (
          <span key={servicio} className="filter-tag">Servicio: {servicio}</span>
        ))}
      </>
    );
  };

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h2 className="title-registro">Registro Usuarios</h2>
        <div className="rightActions">
          <TextField
            placeholder="Buscar..."
            size="small"
            variant="outlined"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeleted}
                onChange={handleShowDeletedChange}
                color="primary"
              />
            }
            label="Mostrar borrados"
          />
          <Link to="/vigilantes/new" className="link">
            Nuevo perfil
          </Link>
        </div>
      </div>

      {/* Mostrar mensaje de error si existe */}
      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          margin: '10px 0', 
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Componente de filtros */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <VigilanteFilters
          onFilterApply={handleFilterApply}
          isLoading={isLoading}
        />

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Divider style={{ width: '80%', margin: '8px 0' }}>
            <Typography variant="body2" color="textSecondary" style={{ padding: '0 10px' }}>
              o cargar usuarios
            </Typography>
          </Divider>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <AdminVigilantesButton
            onClick={handleLoadAdminVigilantes}
            isLoading={isLoading}
          />
          <LoadAllButton
            onClick={handleLoadAllVigilantes}
            isLoading={isLoading}
          />
        </div>
      </div>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        (currentDataSource || 
         (advancedFilters.clientes && advancedFilters.clientes.length > 0) ||
         (advancedFilters.servicios && advancedFilters.servicios.length > 0) ||
         advancedFilters.cliente === 'todos') ? (
          filteredData.length > 0 ? (
            <>
              <div className="filter-summary">
                <p>
                  <FilterListIcon style={{ marginRight: '8px', fontSize: '18px' }} />
                  Mostrando {filteredData.length} usuario{filteredData.length !== 1 ? 's' : ''}
                  {renderFilterTags()}
                </p>
              </div>
              <DataGridPro
                className="datagrid"
                rows={filteredData}
                columns={userColumns.concat(actionColumn)}
                pageSize={100}
                rowsPerPageOptions={[9]}
                checkboxSelection
                localeText={customLocaleText}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: false,
                    },
                  },
                }}
                slots={{ toolbar: GridToolbar }}
                onRowSelectionModelChange={onSelectionModelChange}
              />
            </>
          ) : (
            <div className="empty-state">
              <SearchIcon className="empty-state-icon" />
              <div className="empty-state-title">No se encontraron usuarios</div>
              <div className="empty-state-description">
                No hay usuarios que coincidan con los criterios seleccionados. Intente con diferentes opciones.
              </div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleFilterApply({ clientes: [], servicios: [] })}
                className="empty-state-action"
              >
                Limpiar filtros
              </Button>
            </div>
          )
        ) : (
          <div className="empty-state">
            <FilterListIcon className="empty-state-icon" />
            <div className="empty-state-title">Seleccione una opción para ver usuarios</div>
            <div className="empty-state-description">
              Utilice los filtros de arriba para buscar usuarios por cliente y/o servicio,
              o use los botones para mostrar usuarios según sus permisos o todos los usuarios del sistema.
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default Datatable;